import React, { type ComponentProps, Suspense } from 'react'

import { typeSafeObjectKeys } from '../../../util/typeSafeObjectKeys'
import type { TextColorOverride } from '../TextColorOverride'
import { getIconSizeForType } from '../utils/getIconSizeForType'
import { getLazyFallbackIconForSize } from '../utils/getLazyFallbackIconForSize'
import { CoreIcons, LazyIcons } from './icons'
import {
  type Icon,
  type Icon12,
  type Icon16,
  type Icon24,
  type Icon32,
  type Icon40,
  ICONS_LIST,
} from './icons/directory'

type Props = {
  type: Icon
  color?: ComponentProps<typeof TextColorOverride>['color']
}

export function FoundationIcon({ color, type }: Props) {
  const CoreIconTypes: Icon[] = typeSafeObjectKeys(CoreIcons)
  const isCoreIconType = (
    iconType: Icon
  ): iconType is keyof typeof CoreIcons => {
    return CoreIconTypes.includes(iconType)
  }

  if (isCoreIconType(type)) {
    const Component = CoreIcons[type]
    return <Component color={color} />
  }
  /**
   * We assume that any icon that is not found in CoreIcons
   * must be lazy loaded
   */
  const LazyComponent = LazyIcons[type]

  const suspenseDivSize = `${getIconSizeForType(type)}px`
  return (
    <Suspense fallback={getLazyFallbackIconForSize(suspenseDivSize)}>
      <LazyComponent color={color} />
    </Suspense>
  )
}

export function getIsSize12Icon(type: Icon): type is Icon12 {
  return type.endsWith('12')
}

export function getIsSize16Icon(type: Icon): type is Icon16 {
  return type.endsWith('16')
}

export function getIsSize24Icon(type: Icon): type is Icon24 {
  return type.endsWith('24')
}

export function getIsSize32Icon(type: Icon): type is Icon32 {
  return type.endsWith('32')
}

export function getIsSize40Icon(type: Icon): type is Icon40 {
  return type.endsWith('40')
}

export function isValidFoundationIconType(name: string): name is Icon {
  return ICONS_LIST.includes(name as any)
}
